import { PriceChange } from '@toggle/toggle';
import { addDays, format, getDay, nextMonday } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';

import { getMarketHours } from '~/components/price-display-view/helpers/price-display-helpers';
import { useMarketReactionDates } from '~/modules/market-reaction-charts/hooks/useMarketReactionDates';
import { usePriceHistory } from '~/react-query-hooks/prices/usePriceHistory';
import { Tracking } from '~/services/tracking';

export const NEW_YORK_TIMEZONE = 'America/New_York';
const DATE_FORMAT = 'yyyy-MM-dd';

interface UsePriceMarketReactionDataProps {
  entityTag: string;
  ticker: string;
  reportingDate: string;
  enabled?: boolean;
  isMultipleEntitiesChart?: boolean;
}

export type PriceByDay = Record<string, PriceChange>;

export const usePriceMarketReactionData = ({
  entityTag,
  ticker,
  reportingDate,
  enabled = true,
  isMultipleEntitiesChart = false,
}: UsePriceMarketReactionDataProps) => {
  const reportingDateET = toZonedTime(
    new Date(reportingDate),
    NEW_YORK_TIMEZONE
  );
  const { isPreMarket: isReportingPreMarket } = getMarketHours(
    new Date(reportingDate)
  );
  const { fromDateUTC, toDateUTC } = useMarketReactionDates(reportingDate);

  const { data: priceHistoryData, isLoading: isPriceHistoryLoading } =
    usePriceHistory({
      entityTag,
      ticker,
      resolution: '1m',
      dateFrom: fromDateUTC.toISOString(),
      dateTo: toDateUTC.toISOString(),
      enabled,
    });

  const { data: prevCloseHistoryData, isLoading: isPrevCloseLoading } =
    usePriceHistory({
      entityTag,
      ticker,
      resolution: '1D',
      dateFrom: addDays(fromDateUTC, -4).toISOString(),
      dateTo: addDays(toDateUTC, 4).toISOString(),
      enabled: !isMultipleEntitiesChart,
    });

  const isLoading = isPriceHistoryLoading || isPrevCloseLoading;

  const priceByDay = useMemo(() => {
    if (!Array.isArray(priceHistoryData)) {
      if (priceHistoryData !== undefined) {
        Tracking.captureException(
          new Error(
            'usePriceMarketReactionData: priceHistoryData is not an array'
          ),
          {
            entityTag,
            ticker,
            reportingDate,
            dataType: typeof priceHistoryData,
            dataValue: JSON.stringify(priceHistoryData)?.slice(0, 200),
          }
        );
      }

      return undefined;
    }

    return priceHistoryData.reduce<PriceByDay>((acc, point) => {
      const zonedDate = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
      const day = format(zonedDate, DATE_FORMAT);

      if (!acc[day]) {
        acc[day] = [];
      }

      acc[day].push({ time: point.time, value: point.close });

      return acc;
    }, {});
  }, [priceHistoryData]);

  if (!priceByDay || Object.keys(priceByDay).length === 0) {
    return {
      priceData: [],
      fromDateEST: undefined,
      toDateEST: undefined,
      lastProportionChange: undefined,
      prevCloseDate: undefined,
      lastKnownPrice: undefined,
      lastKnownDate: undefined,
      isLoading,
    };
  }

  const days = Object.keys(priceByDay);
  // Previous day for pre-market insights
  const theoreticalFromDate = isReportingPreMarket
    ? addDays(reportingDateET, -1)
    : reportingDateET;
  const theoreticalFromDateStr = format(theoreticalFromDate, DATE_FORMAT);
  const directIndex = days.indexOf(theoreticalFromDateStr);
  // Fallback on previous day with data to handle weekends or holidays
  const fromIndex =
    directIndex >= 0
      ? directIndex
      : Math.max(
          days.findLastIndex(day => day <= theoreticalFromDateStr),
          0
        );

  const fromPriceData = (priceByDay[days[fromIndex]] ?? []).filter(point => {
    const zonedTime = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
    const hours = zonedTime.getHours();
    const minutes = zonedTime.getMinutes();
    return hours > 9 || (hours === 9 && minutes >= 30);
  });

  const toDay = days[fromIndex + 1];
  const toPriceData = toDay
    ? priceByDay[toDay].filter(point => {
        const zonedTime = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
        const hours = zonedTime.getHours();
        const minutes = zonedTime.getMinutes();
        return hours < 16 || (hours === 16 && minutes === 0);
      })
    : [];

  const fromDateForFallback = new Date(`${days[fromIndex]}T16:00:00`);
  const dayOfWeek = getDay(fromDateForFallback);
  const fallbackToDate = [0, 5, 6].includes(dayOfWeek)
    ? nextMonday(fromDateForFallback)
    : addDays(fromDateForFallback, 1);

  const priceData = [...fromPriceData, ...toPriceData];
  const lastKnownData = priceData[priceData.length - 1];

  const startOfDayUTC = priceData[0]?.time?.replace(/T.*/, 'T00:00:00Z');

  const prevCloseData = prevCloseHistoryData?.findLast(h =>
    startOfDayUTC ? h.time < startOfDayUTC : false
  );

  const lastKnownPrice = lastKnownData?.value;
  const lastProportionChange =
    lastKnownPrice && prevCloseData?.close
      ? ((lastKnownPrice - prevCloseData.close) / prevCloseData.close) * 100
      : undefined;

  return {
    priceData,
    fromDateEST: new Date(`${days[fromIndex]}T09:30:00`),
    toDateEST: toDay ? new Date(`${toDay}T16:00:00`) : fallbackToDate,
    lastProportionChange,
    prevCloseData,
    lastKnownPrice,
    lastKnownDate: lastKnownData?.time,
    isLoading,
  };
};
